// One controller owns Google loading, consent, and manual Gatsby page views.
// Keep browser globals inside functions so this module is safe during SSR.
const CONSENT_KEY = "milky.analyticsConsent.v1"
const CONSENT_AGE = 180 * 24 * 60 * 60 * 1000
const ORIGIN = "https://milkyfelix.com"
const PRODUCTION_ID = "G-X8Q0M8C8RX"
const CAMPAIGN_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]
const subscribers = new Set()
let consent
let consentExpires = 0
let currentPage
let lastPage
let routeToken = 0
let firstRoute = true
let script
let stopped = false
let expiryTimer

const inBrowser = () => typeof window !== "undefined" && typeof document !== "undefined"
const measurementId = () => process.env.GATSBY_GA_MEASUREMENT_ID || ""

function isAnalyticsAvailable() {
  if (!inBrowser() || process.env.NODE_ENV !== "production" || process.env.GATSBY_GA_ENABLED !== "true") return false
  const id = measurementId()
  if (!/^G-[A-Z0-9]+$/.test(id)) return false
  const host = window.location.hostname
  if (host === "milkyfelix.com" || host === "www.milkyfelix.com") return true
  return (host === "localhost" || host === "127.0.0.1") &&
    process.env.GATSBY_GA_DEBUG === "true" && id !== PRODUCTION_ID
}

function readChoice() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(CONSENT_KEY))
    if (saved?.version === 1 && ["accepted", "denied"].includes(saved.choice) &&
        Number.isFinite(saved.expiresAt) && saved.expiresAt > Date.now()) return saved
  } catch { /* Storage can be unavailable. The visitor can still choose for this tab. */ }
  return { choice: "unknown", expiresAt: 0 }
}

function notify() {
  subscribers.forEach(listener => listener())
}

function scheduleExpiry() {
  window.clearTimeout(expiryTimer)
  if (!consentExpires) return
  // Browser timers cap at about 24 days; renew the timer until the choice expires.
  expiryTimer = window.setTimeout(() => {
    getConsent()
    if (consent !== "unknown") scheduleExpiry()
  }, Math.min(Math.max(consentExpires - Date.now(), 1), 2147483647))
}

function getConsent() {
  if (!inBrowser()) return "unknown"
  if (consent === undefined) {
    const saved = readChoice()
    consent = saved.choice
    consentExpires = saved.expiresAt
    scheduleExpiry()
    window.addEventListener("storage", event => {
      if (event.key !== CONSENT_KEY && event.key !== null) return
      const previous = consent
      const next = readChoice()
      consent = next.choice
      consentExpires = next.expiresAt
      if (previous === "accepted" && consent !== "accepted") stopTracking()
      scheduleExpiry()
      notify()
      if (consent === "accepted") deliverPage()
    })
  }
  if (consentExpires && consentExpires <= Date.now()) {
    consent = "unknown"
    consentExpires = 0
    stopTracking()
    notify()
  }
  return consent
}

function subscribeConsent(listener) {
  subscribers.add(listener)
  return () => subscribers.delete(listener)
}

function sanitizePageUrl(value, includeCampaign = false) {
  try {
    const url = new URL(typeof value === "string" ? value : value.href, ORIGIN)
    const pathname = url.pathname.replace(/\/+$/, "") + "/"
    const safe = new URL(pathname, ORIGIN)
    if (includeCampaign) CAMPAIGN_KEYS.forEach(key => {
      const value = url.searchParams.get(key)
      if (value) safe.searchParams.set(key, value)
    })
    return safe.href
  } catch { return ORIGIN + "/" }
}

function sanitizeReferrer(value) {
  try {
    const url = new URL(value)
    return /^https?:$/.test(url.protocol) ? url.origin + url.pathname : ""
  } catch { return "" }
}

function contentGroup(pathname) {
  if (pathname === "/") return "home"
  if (pathname === "/services/") return "services"
  if (pathname === "/blog/") return "blog_index"
  if (pathname.startsWith("/blog/tags/")) return "blog_tag"
  if (pathname.startsWith("/blog/")) return "blog_article"
  if (pathname === "/privacy/") return "privacy"
  return "other"
}

function command() {
  window.dataLayer.push(arguments)
}

function loadTag(context) {
  if (script || stopped) return
  const id = measurementId()
  window[`ga-disable-${id}`] = false
  window.dataLayer = window.dataLayer || []
  window.gtag = command
  command("consent", "default", {
    analytics_storage: "denied", ad_storage: "denied",
    ad_user_data: "denied", ad_personalization: "denied",
  })
  command("consent", "update", { analytics_storage: "granted" })
  command("set", context)
  command("js", new Date())
  command("config", id, {
    // Page context stays in global `set`: config-scoped values would override
    // later route updates for native engagement and enhanced events.
    send_page_view: false,
    allow_google_signals: false, allow_ad_personalization_signals: false,
    ...(process.env.GATSBY_GA_DEBUG === "true" ? { debug_mode: true } : {}),
  })
  script = document.createElement("script")
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
  script.onerror = () => {
    stopped = true
    window[`ga-disable-${id}`] = true
    window.dataLayer.length = 0
  }
  document.head.appendChild(script)
}

function deliverPage() {
  if (!isAnalyticsAvailable() || getConsent() !== "accepted" || stopped || !currentPage?.ready) return
  if (lastPage?.path === currentPage.path) return
  const context = {
    page_location: currentPage.url,
    page_title: document.title,
    page_referrer: lastPage ? sanitizeReferrer(lastPage.context.page_location) : sanitizeReferrer(document.referrer),
    content_group: contentGroup(currentPage.path),
  }
  loadTag(context)
  command("set", context)
  command("event", "page_view", context)
  lastPage = { path: currentPage.path, context }
}

function recordPage(location) {
  if (!inBrowser()) return
  const url = sanitizePageUrl(location, firstRoute)
  firstRoute = false
  const path = new URL(url).pathname
  // Keep the landing campaign when Gatsby repeats the same route callback.
  if (currentPage?.path !== path) currentPage = { path, url, ready: false }
  const token = ++routeToken
  window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
    if (token !== routeToken) return
    currentPage.ready = true
    deliverPage()
  }))
}

function clearGoogleCookies() {
  const names = document.cookie.split(";").map(cookie => cookie.trim().split("=")[0])
    .filter(name => name === "_ga" || name.startsWith("_ga_"))
  const host = window.location.hostname
  const domains = ["", `; Domain=${host}`, `; Domain=.${host}`]
  if (host === "www.milkyfelix.com") domains.push("; Domain=milkyfelix.com", "; Domain=.milkyfelix.com")
  names.forEach(name => domains.forEach(domain => {
    document.cookie = `${name}=; Max-Age=0; Path=/${domain}; SameSite=Lax`
  }))
}

function stopTracking() {
  if (!script) return false
  stopped = true
  window[`ga-disable-${measurementId()}`] = true
  window.dataLayer.length = 0
  script.remove()
  clearGoogleCookies()
  return true
}

function setConsent(choice) {
  if (!inBrowser() || !["accepted", "denied"].includes(choice)) return { persisted: false }
  getConsent()
  // Disable collection before any storage access or UI updates.
  const wasRunning = choice === "denied" && stopTracking()
  consent = choice
  consentExpires = Date.now() + CONSENT_AGE
  let persisted = true
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify({ version: 1, choice, expiresAt: consentExpires }))
  } catch { persisted = false }
  scheduleExpiry()
  notify()
  if (choice === "accepted") {
    // A removed/failed tag must be restarted in a fresh document.
    if (stopped && persisted) window.location.reload()
    else deliverPage()
  }
  if (wasRunning && persisted) window.location.reload()
  return { persisted, reloadRequired: choice === "accepted" && stopped }
}

function openAnalyticsPreferences() {
  if (inBrowser()) window.dispatchEvent(new Event("milky:open-analytics-preferences"))
}

function trackEvent(name, params = {}) {
  if (!isAnalyticsAvailable() || getConsent() !== "accepted" || stopped || !lastPage) return
  let allowed
  if (name === "contact_click" && ["header_desktop", "header_mobile", "hero"].includes(params.placement)) {
    allowed = { placement: params.placement }
  } else if (name === "generate_lead" && params.form_id === "contact" && ["home", "services"].includes(params.form_location)) {
    allowed = { form_id: "contact", form_location: params.form_location }
  } else return
  command("event", name, { ...lastPage.context, ...allowed })
}

module.exports = {
  recordPage, getConsent, setConsent, subscribeConsent, openAnalyticsPreferences,
  trackEvent, sanitizePageUrl, isAnalyticsAvailable,
}
